import { useEffect, useRef } from "react";
import { X } from "@phosphor-icons/react";
import { api } from "../lib/api";

const toEmbed = (url) => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

const EMBED_ASSUMED_SECONDS = 45;

export const VideoPlayer = ({ video, onClose, onProgress, children }) => {
  const src = video.url.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${video.url}`
    : video.url;
  const embed = video.source_type === "link" ? toEmbed(video.url) : null;
  const lastPosted = useRef(0);

  const track = (raw) => {
    const pct = Math.min(100, Math.round(raw));
    if (onProgress) onProgress(video.id, pct);
    if (pct >= 100 && lastPosted.current < 100 ||
        pct - lastPosted.current >= 10 ||
        (pct >= 80 && lastPosted.current < 80)) {
      lastPosted.current = pct;
      api.post(`/videos/${video.id}/watch`, { pct }).catch(() => {});
    }
  };

  useEffect(() => {
    if (!embed) return;
    const start = Date.now();
    const t = setInterval(() => {
      track(((Date.now() - start) / 1000 / EMBED_ASSUMED_SECONDS) * 100);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/70 flex items-center justify-center p-4 sm:p-8" onClick={onClose}>
      <div data-testid="video-player-modal" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl rise-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <p className="font-heading font-bold text-lg leading-snug pr-4">{video.title}</p>
          <button data-testid="video-player-close" onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 tap-press shrink-0"><X size={24} /></button>
        </div>
        <div className="p-4 sm:p-6">
          {embed ? (
            <iframe data-testid="embed-video-player" title={video.title} src={embed}
              className="w-full aspect-video rounded-xl bg-black" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          ) : (
            <video data-testid="html5-video-player" controls autoPlay playsInline
              onTimeUpdate={(e) => e.target.duration && track((e.target.currentTime / e.target.duration) * 100)}
              onEnded={() => track(100)}
              className="w-full aspect-video rounded-xl bg-black" src={src} />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
